<template>
  <div class="crud-form">
    <v-row>
      <v-col cols="12" :lg="sideSections.length ? 8 : 12" class="py-0 crud-form__col">
        <template v-for="section in mainSections">
          <v-card v-if="section.key !== 'english' || showEnglish" :key="section.key" class="settings-card">
            <v-card-title>
              <v-icon left>{{ section.icon }}</v-icon>{{ section.title }}
              <template v-if="section.key === 'english'">
                <v-spacer></v-spacer>
                <english-fields-toggle small></english-fields-toggle>
              </template>
            </v-card-title>
            <v-card-subtitle v-if="section.key === 'english'">
              اختیاری - هر فیلدی که خالی بماند، مقدار فارسی آن ذخیره می‌شود.
            </v-card-subtitle>
            <v-card-text>
              <v-row>
                <v-col v-for="field in section.fields" :key="field.name"
                       cols="12" :md="field.cols" class="py-0">
                  <FieldRenderer :field="field" :values="values" @input="handleInput"/>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </template>

        <!-- Extra cards a page adds to the form (e.g. product specifications). -->
        <slot></slot>

        <!-- Collapsed English section: says what happens without it. -->
        <div v-if="hasEnglish && !showEnglish" class="english-collapsed sunken-block">
          <v-icon color="#00ADB5">mdi-translate</v-icon>
          <div class="english-collapsed__text">
            <div>نسخه انگلیسی</div>
            <div class="english-collapsed__hint">اختیاری - در صورت خالی بودن، مقدار فارسی ذخیره می‌شود.</div>
          </div>
          <english-fields-toggle small></english-fields-toggle>
        </div>
      </v-col>

      <v-col v-if="sideSections.length" cols="12" lg="4" class="py-0 crud-form__col">
        <v-card v-for="section in sideSections" :key="section.key" class="settings-card">
          <v-card-title><v-icon left>{{ section.icon }}</v-icon>{{ section.title }}</v-card-title>
          <v-card-text>
            <FieldRenderer v-for="field in section.fields" :key="field.name"
                           :field="field" :values="values" @input="handleInput"/>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <div class="crud-form__savebar">
      <v-btn class="btn-primary" large depressed @click="sendRequest">
        <v-icon left>mdi-content-save</v-icon>
        ذخیره
      </v-btn>
    </div>
  </div>
</template>

<script>
import FieldRenderer from "@/components/General/FieldRenderer";
import EnglishFieldsToggle from "@/components/General/EnglishFieldsToggle.vue";

// Forms are described as flat field lists (optionally grouped in `row`s for
// layout). Fields are sorted into these cards by `field.section` or, when it
// is absent, by name and type, so the views did not have to change.
const SECTIONS = [
  {key: 'main', title: 'اطلاعات اصلی', icon: 'mdi-text-box-outline', side: false},
  {key: 'content', title: 'توضیحات', icon: 'mdi-text-long', side: false},
  {key: 'seo', title: 'سئو', icon: 'mdi-web', side: false},
  {key: 'english', title: 'نسخه انگلیسی', icon: 'mdi-translate', side: false},
  {key: 'media', title: 'تصویر', icon: 'mdi-image-outline', side: true},
  {key: 'settings', title: 'تنظیمات', icon: 'mdi-tune', side: true},
]

function sectionOf(field) {
  if (field.section) return field.section
  if (field.name.startsWith('en_')) return 'english'
  if (field.name.includes('meta')) return 'seo'
  if (field.type === 'ckeditor') return 'content'
  if (field.type === 'image') return 'media'
  if (field.type === 'select' || field.type === 'checkbox') return 'settings'
  return 'main'
}

export default {
  name: "CrudGenerator",
  components: {
    FieldRenderer,
    EnglishFieldsToggle,
  },
  props: ["fields", "currentValues", "name"],
  data() {
    return {
      values: {...this.currentValues}
    };
  },
  computed: {
    flatFields() {
      return (this.fields || []).flatMap((field) => field.type === 'row' ? field.fields : [field])
    },

    sections() {
      return SECTIONS
          .map((section) => ({
            ...section,
            fields: this.flatFields.filter((field) => sectionOf(field) === section.key),
          }))
          .filter((section) => section.fields.length)
    },

    mainSections() {
      return this.sections.filter((section) => !section.side)
    },

    sideSections() {
      return this.sections.filter((section) => section.side)
    },

    hasEnglish() {
      return this.sections.some((section) => section.key === 'english')
    },

    showEnglish() {
      return this.$store.state.showEnglishFields
    },
  },
  methods: {
    /** Update the values object when input event is emitted */
    handleInput({name, value}) {
      this.$set(this.values, name, value);
    },

    sendRequest() {
      this.$emit("submit", this.values);
    }
  }
};
</script>

<style scoped>
.english-collapsed {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  margin-bottom: 24px;
}

.english-collapsed__text {
  flex: 1;
}

.english-collapsed__hint {
  font-size: 13px;
  color: var(--c-muted);
}

/* Stays reachable on long forms (and above the specification card that some
   pages render after the form). */
.crud-form__savebar {
  position: sticky;
  bottom: 0;
  z-index: 5;
  display: flex;
  justify-content: flex-end;
  padding: 12px 0;
  margin-bottom: 16px;
  background: linear-gradient(to top, #222831 70%, rgba(34, 40, 49, 0));
}

@media (max-width: 600px) {
  .crud-form__col {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  .english-collapsed {
    flex-wrap: wrap;
  }

  .crud-form__savebar .v-btn {
    flex: 1;
  }
}
</style>
